import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { api } from '../api/client'
import type { AvailableVolunteer } from '../api/generated/AvailableVolunteer'
import type { LiveShift } from '../api/generated/LiveShift'
import type { NewOffer } from '../api/generated/NewOffer'
import { useAction } from '../api/useAction'
import { useApi } from '../api/useApi'
import { volunteerName } from '../assignments/status'
import { formatRange } from '../shifts/time'
import { AvailableChip, WishedPositionChip } from '../volunteers/chips'
import { useLiveReload } from './useLiveReload'

const messages = {
  not_found: 'Ce créneau est terminé.',
  volunteer_unavailable: "Ce bénévole n'est plus disponible sur ce créneau.",
}

/** Available volunteers of a shift, to ask one of them to take it. */
export function ReplacementDialog({
  slug,
  shift,
  onClose,
}: {
  slug: string
  shift: Pick<LiveShift, 'id' | 'position_name' | 'starts_at' | 'ends_at'>
  onClose: () => void
}) {
  const path = `/events/${slug}/shifts/${shift.id}`
  const volunteers = useApi<AvailableVolunteer[]>(`${path}/available`, messages)
  useLiveReload(slug, volunteers.reload)
  const { error, pending, run } = useAction(messages)
  const ask = (volunteer: AvailableVolunteer) =>
    run(async () => {
      const body: NewOffer = { user_id: volunteer.user_id }
      volunteers.setData(await api.post<AvailableVolunteer[]>(`${path}/offers`, body))
    })

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Remplacer : {shift.position_name}</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            {formatRange(shift.starts_at, shift.ends_at)}. Bénévoles qui ne sont pas en poste à
            cette heure, disponibles d'abord : la personne choisie reçoit une notification pour
            accepter ou refuser.
          </Typography>
          {volunteers.error && <Alert severity="error">{volunteers.error}</Alert>}
          {volunteers.data?.length === 0 && (
            <Typography color="text.secondary">Aucun bénévole disponible.</Typography>
          )}
          <List dense disablePadding>
            {volunteers.data?.map((volunteer) => (
              <ListItem key={volunteer.user_id} disableGutters sx={{ flexWrap: 'wrap', gap: 1 }}>
                <ListItemText
                  primary={volunteerName(volunteer)}
                  secondary={
                    volunteer.phone && (
                      <Link href={`tel:${volunteer.phone}`}>{volunteer.phone}</Link>
                    )
                  }
                />
                {volunteer.within_availability && <AvailableChip />}
                {volunteer.interested && <WishedPositionChip />}
                {volunteer.status === 'offered' ? (
                  <Chip size="small" label="Demande envoyée" />
                ) : (
                  <>
                    {volunteer.status === 'withdrawn' && (
                      <Chip size="small" variant="outlined" label="Retiré" />
                    )}
                    <Button size="small" disabled={pending} onClick={() => ask(volunteer)}>
                      {volunteer.status === 'withdrawn' ? 'Redemander' : 'Demander'}
                    </Button>
                  </>
                )}
              </ListItem>
            ))}
          </List>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  )
}
